/**
 * PATH: packages/job-hub/src/jobs/qa/qaBackgroundWrite.ts
 * FILE_ID: JOBHUB-QA-BACKGROUND-WRITE-001
 * PURPOSE: Post a big QA edit as a background job and wait for its response.
 * WHY: On production a 35-area template insert is about 35 s of database time,
 *      past the 30 s statement and gateway limits. The backend runs these edits
 *      in Celery when asked with ?background=1 (change #1073) and the client polls
 *      /api/qa/background-jobs/<id>/.
 * LANDMINE: The opt-in is a query param, not a header: a new header fails the
 *      cross-origin preflight, and the cookie-based Jobs client drops headers.
 *      A server without the route answers inline; that answer is returned as is.
 *      A failed job throws an axios-shaped `response` so callers' handlers work.
 */
import type { JobHubHttpClient, JobHubHttpResponse } from '../jobHubDataAdapter'

const POLL_FIRST_MS = 700
const POLL_MAX_MS = 3_000
const WAIT_LIMIT_MS = 10 * 60_000
// A phone on site drops a request now and then while the job keeps running.
const POLL_FAILURES_ALLOWED = 5
// A server that ignores ?background=1 runs the edit inline, as before.
const INLINE_FALLBACK_TIMEOUT_MS = 120_000

interface QaBackgroundJob {
  job_id: string
  state: 'queued' | 'running' | 'done' | 'failed'
  status?: number
  data?: unknown
}

export class QaBackgroundWriteError extends Error {
  response: { status: number; data: unknown }

  constructor(status: number, data: unknown) {
    super(`QA change failed (${status})`)
    this.name = 'QaBackgroundWriteError'
    this.response = { status, data }
  }
}

export interface QaBackgroundWriteOptions {
  wait?: (ms: number) => Promise<void>
  now?: () => number
}

function isQueuedJob(data: unknown): data is QaBackgroundJob {
  if (!data || typeof data !== 'object') return false
  const row = data as Record<string, unknown>
  return typeof row.job_id === 'string' && typeof row.status_url === 'string'
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export async function postQaInBackground<T>(
  client: JobHubHttpClient,
  url: string,
  body: unknown,
  headers: Record<string, string> | undefined,
  options: QaBackgroundWriteOptions = {},
): Promise<JobHubHttpResponse<T>> {
  const wait = options.wait ?? sleep
  const now = options.now ?? Date.now
  const first = await client.post(url, body, {
    headers,
    params: { background: 1 },
    timeout: INLINE_FALLBACK_TIMEOUT_MS,
  })
  if (!isQueuedJob(first.data)) return first as JobHubHttpResponse<T>

  const jobUrl = `/api/qa/background-jobs/${first.data.job_id}/`
  const deadline = now() + WAIT_LIMIT_MS
  let delay = POLL_FIRST_MS
  let failures = 0
  while (now() < deadline) {
    await wait(delay)
    delay = Math.min(Math.round(delay * 1.5), POLL_MAX_MS)
    let poll: JobHubHttpResponse<unknown>
    try {
      poll = await client.get(jobUrl, { headers })
      failures = 0
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      failures += 1
      if (status === 404 || failures > POLL_FAILURES_ALLOWED) throw error
      continue
    }
    const job = poll.data as QaBackgroundJob
    if (job.state !== 'done' && job.state !== 'failed') continue
    const status = job.status ?? (job.state === 'failed' ? 500 : 200)
    if (status >= 400) throw new QaBackgroundWriteError(status, job.data)
    return { data: job.data as T, status }
  }
  throw new QaBackgroundWriteError(504, {
    error: 'This change is still running. Refresh in a minute to see it.',
  })
}
